/**
 * Phase 3: Secure API Client
 *
 * Access token: short-lived (15 min), stored in memory only — never localStorage.
 * Refresh token: long-lived (30 days), HttpOnly Secure cookie — handled by browser.
 * On 401: automatically attempt /auth/refresh, retry original request once.
 * X-Request-Id: generated per-request for end-to-end traceability.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8787"

// ---- In-memory token store (never persisted to localStorage/sessionStorage) ----

let _accessToken: string | null = null
let _accessTokenExpiresAt: string | null = null
let _refreshPromise: Promise<boolean> | null = null

export function setAccessToken(token: string | null, expiresAt?: string | null) {
  _accessToken = token
  _accessTokenExpiresAt = expiresAt ?? null
}

export function getAccessToken(): string | null {
  // Check if expired
  if (_accessToken && _accessTokenExpiresAt) {
    const expiresMs = new Date(_accessTokenExpiresAt).getTime()
    // Add 30-second buffer to avoid race conditions
    if (Date.now() > expiresMs - 30_000) {
      _accessToken = null
      _accessTokenExpiresAt = null
    }
  }
  return _accessToken
}

export function clearAccessToken() {
  _accessToken = null
  _accessTokenExpiresAt = null
}

// ---- Request ID generation ----

function generateRequestId(): string {
  const ts = Date.now().toString(36)
  const rnd = Math.random().toString(36).slice(2, 8)
  return `web-${ts}-${rnd}`
}

// ---- Core request function with auto-refresh ----

async function rawRequest<T = unknown>(
  path: string,
  options: RequestInit = {},
  skipAuth = false,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`
  const headers = new Headers(options.headers)
  const requestId = generateRequestId()
  headers.set("X-Request-Id", requestId)

  if (!skipAuth) {
    const token = getAccessToken()
    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }
  }
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "include", // sends HttpOnly cookies (refresh token)
  })

  // Capture server-side request_id from response header
  const serverRequestId = res.headers.get("X-Request-Id") || requestId

  if (res.status === 204) {
    return { ok: true, request_id: serverRequestId } as T
  }

  const data = await res.json().catch(() => ({ ok: false, error: "Invalid response" }))

  // Always attach request_id to response for error traceability
  if (typeof data === "object" && data !== null) {
    data.request_id = data.request_id || serverRequestId
  }

  if (!res.ok && !data.error) {
    data.error = `Request failed (${res.status})`
  }

  return data as T
}

// ---- Error helper for toasts ----

/**
 * Format an API error for display in a toast.
 * Returns { message, requestId } where requestId can be shown for support reference.
 */
export function formatApiError(result: Record<string, unknown>): { message: string; requestId: string } {
  const error = typeof result.error === "string" ? result.error : "An unexpected error occurred"
  const requestId = typeof result.request_id === "string" ? result.request_id : ""
  return { message: error, requestId }
}

export async function requestWithRefresh<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // First attempt
  const result = await rawRequest<T>(path, options)

  // If 401 and we had a token, try refreshing
  const resultObj = result as Record<string, unknown>
  if (resultObj.error && typeof resultObj.error === "string" &&
      (resultObj.error.includes("expired") || resultObj.error.includes("Invalid session") || resultObj.error.includes("Missing session") || resultObj.error === "Unauthorized.")) {
    const refreshed = await attemptRefresh()
    if (refreshed) {
      // Retry with new token
      return rawRequest<T>(path, options)
    }
  }

  return result
}

async function attemptRefresh(): Promise<boolean> {
  // Deduplicate concurrent refresh calls
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    try {
      const res = await rawRequest<{
        ok: boolean
      access_token?: string
      access_token_expires_at?: string
      user?: { id: string; easy_id: string; email: string; name: string; role: string; is_platform_admin?: boolean; is_ops_admin?: boolean }
      property_id?: string
      properties?: Array<{ id: string; name: string; [k: string]: unknown }>
    }>("/auth/refresh", { method: "POST" }, true)

      if (res.ok && res.access_token) {
        setAccessToken(res.access_token, res.access_token_expires_at ?? null)
        // Notify auth context of refreshed state
        if (_onRefreshCallback) {
          _onRefreshCallback(res)
        }
        return true
      }
      clearAccessToken()
      return false
    } catch {
      clearAccessToken()
      return false
    }
  })()

  try {
    return await _refreshPromise
  } finally {
    _refreshPromise = null
  }
}

// Callback for AuthProvider to update its state on token refresh
type RefreshCallback = (data: {
  user?: { id: string; easy_id: string; email: string; name: string; role: string; is_platform_admin?: boolean; is_ops_admin?: boolean }
  property_id?: string
  properties?: Array<{ id: string; name: string; [k: string]: unknown }>
}) => void

let _onRefreshCallback: RefreshCallback | null = null
export function setOnRefreshCallback(cb: RefreshCallback | null) {
  _onRefreshCallback = cb
}

// ---- Public API client ----

export const apiClient = {
  // Auth
  login(email: string, password: string) {
    return rawRequest<{
      ok: boolean
      error?: string
      access_token?: string
      access_token_expires_at?: string
      token?: string // legacy compat
      logged_in?: boolean
      user?: { id: string; easy_id: string; email: string; name: string; role: string; is_platform_admin?: boolean; is_ops_admin?: boolean }
      property_id?: string
      properties?: Array<{ id: string; name: string; [k: string]: unknown }>
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }, true) // skipAuth — no bearer for login
  },

  demoLogin() {
    return rawRequest<{
      ok: boolean
      error?: string
      seeded?: boolean
    }>("/auth/demo", {
      method: "POST",
    }, true) // skipAuth — seeds demo user if not exists
  },

  register(name: string, email: string, password: string, chatVisitorId?: string) {
    return rawRequest<{
      ok: boolean
      error?: string
      logged_in?: boolean
      verification_required?: boolean
      email?: string
      email_sent?: boolean
      message?: string
      activation_expires_at?: string
    }>("/auth/session", {
      method: "POST",
      body: JSON.stringify({
        action: "register", name, email, password,
        ...(chatVisitorId ? { chat_visitor_id: chatVisitorId } : {}),
      }),
    }, true) // skipAuth — no bearer for registration
  },

  activateAccount(token: string) {
    return rawRequest<{
      ok: boolean
      error?: string
      already_active?: boolean
      email?: string
    }>("/api/auth/activate", {
      method: "POST",
      body: JSON.stringify({ token }),
    }, true) // skipAuth — unauthenticated
  },

  resendActivation(email: string) {
    return rawRequest<{
      ok: boolean
      error?: string
      verification_required?: boolean
      message?: string
    }>("/auth/session", {
      method: "POST",
      body: JSON.stringify({ action: "resend_activation", email }),
    }, true)
  },

  forgotPassword(email: string) {
    return rawRequest<{
      ok: boolean
      message?: string
    }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }, true)
  },

  resetPassword(token: string, password: string) {
    return rawRequest<{
      ok: boolean
      error?: string
      message?: string
    }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }, true)
  },

  changePassword(currentPassword: string, newPassword: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      message?: string
    }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    })
  },

  refresh() {
    return attemptRefresh()
  },

  logout() {
    return rawRequest<{ ok: boolean }>("/auth/logout", { method: "POST" })
  },

  getSession() {
    return requestWithRefresh<{
      ok: boolean
      logged_in?: boolean
      user?: { id: string; easy_id: string; email: string; name: string; role: string; is_platform_admin?: boolean; is_ops_admin?: boolean }
      property_id?: string
      active_property?: { id: string; name: string; [k: string]: unknown } | null
      properties?: Array<{ id: string; name: string; [k: string]: unknown }>
    }>("/auth/session")
  },

  switchProperty(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      property_id?: string
      properties?: Array<{ id: string; name: string; [k: string]: unknown }>
    }>("/auth/session", {
      method: "PUT",
      body: JSON.stringify({ property_id: propertyId }),
    })
  },

  // Places
  getPlaces(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      source?: string
      updated_at?: string
      count?: number
      places?: Array<Record<string, unknown>>
    }>(`/app/places${params}`)
  },

  savePlaces(places: Array<Record<string, unknown>>, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; source?: string; updated_at?: string; count?: number }>(
      `/app/places${params}`,
      { method: "PUT", body: JSON.stringify({ places }) },
    )
  },

  placeSearch(query: string) {
    return requestWithRefresh<{ ok: boolean; results?: Array<Record<string, unknown>> }>(
      `/app/osm-search?q=${encodeURIComponent(query)}`,
    )
  },

  /** @deprecated Use placeSearch() instead */
  osmSearch(query: string) {
    return this.placeSearch(query)
  },

  // Google Places API
  googlePlacesAutocomplete(query: string, types?: string) {
    const params = new URLSearchParams({ q: query })
    if (types) params.set("types", types)
    return requestWithRefresh<{
      ok: boolean
      error?: string
      predictions?: Array<{
        place_id: string
        description: string
        structured_formatting?: { main_text: string; secondary_text: string }
      }>
    }>(`/app/google-places-autocomplete?${params.toString()}`)
  },

  googlePlacesDetails(placeId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      name?: string
      address?: string
      lat?: number | null
      lng?: number | null
      utc_offset?: number | null
      timezone?: string
      address_components?: Array<Record<string, unknown>>
    }>(`/app/google-places-details?place_id=${encodeURIComponent(placeId)}`)
  },

  // House Rules
  getHouseRules(propertyId?: string) {
    const url = propertyId
      ? `/api/properties/${encodeURIComponent(propertyId)}/house-rules`
      : `/app/house-rules`
    return requestWithRefresh<{ ok: boolean; rules?: Record<string, unknown>; updated_at?: string }>(url)
  },

  saveHouseRules(rules: Record<string, unknown>, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; updated_at?: string; rules?: Record<string, unknown> }>(
      `/app/house-rules${params}`,
      { method: "PUT", body: JSON.stringify(rules) },
    )
  },

  // ── Wave 1 loops ──

  // Cross-property tasks backlog (user-scoped)
  getTasks() {
    return requestWithRefresh<{ ok: boolean; error?: string; tasks?: Array<Record<string, unknown>> }>(`/app/tasks`)
  },

  createTask(task: Record<string, unknown>) {
    return requestWithRefresh<{ ok: boolean; error?: string; id?: string }>(`/app/tasks`, {
      method: "POST",
      body: JSON.stringify(task),
    })
  },

  updateTaskStatus(taskId: string, status: "todo" | "snoozed" | "done") {
    return requestWithRefresh<{ ok: boolean; error?: string }>(`/app/tasks/${encodeURIComponent(taskId)}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    })
  },

  // Portfolio aggregate (user-scoped)
  getPortfolioSummary() {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      properties?: Array<{
        id: string
        name: string
        address: string
        revenue_30d: number | null
        pace_delta_pct: number | null
        occupancy_30d_pct: number | null
        reputation_score: number | null
        open_actions: number
        urgent_actions: number
        next_arrival: { guest_name: string; arrival_date: string; departure_date: string; nights: number | null; guests: number | null; channel: string } | null
        monthly_revenue: Record<string, number>
      }>
      totals?: {
        revenue_30d: number | null
        revenue_delta_pct: number | null
        avg_occupancy_pct: number | null
        avg_reputation: number | null
        open_actions: number
        urgent_actions: number
      } | null
      arrivals?: Array<{ property_id: string; guest_name: string; arrival_date: string; nights: number | null; guests: number | null; channel: string }>
    }>(`/app/portfolio-summary`)
  },

  // Review replies (draft → approve gate)
  getReviewReplies(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      replies?: Array<{ review_id: string; source: string; draft_text: string; final_text: string; status: string; updated_at: string }>
    }>(`/app/review-replies?property_id=${encodeURIComponent(propertyId)}`)
  },

  draftReviewReply(propertyId: string, source: "airbnb" | "booking", reviewId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; draft?: string; source?: string; model?: string }>(
      `/app/review-replies/draft?property_id=${encodeURIComponent(propertyId)}`,
      { method: "POST", body: JSON.stringify({ source, review_id: reviewId }) },
    )
  },

  approveReviewReply(propertyId: string, source: "airbnb" | "booking", reviewId: string, finalText: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; status?: string; final_text?: string }>(
      `/app/review-replies/approve?property_id=${encodeURIComponent(propertyId)}`,
      { method: "POST", body: JSON.stringify({ source, review_id: reviewId, final_text: finalText }) },
    )
  },

  // Guidebook welcome approval gate
  getGuidebookWelcome(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      mode?: "approved" | "adaptive"
      approved?: { headline: string; message: string; hero_message: string; approved_at: string } | null
      draft?: { headline: string; message: string; hero_message: string } | null
    }>(`/app/guidebook-welcome?property_id=${encodeURIComponent(propertyId)}`)
  },

  approveGuidebookWelcome(propertyId: string, copy: { headline: string; message: string; hero_message?: string }) {
    return requestWithRefresh<{ ok: boolean; error?: string; mode?: string }>(
      `/app/guidebook-welcome/approve?property_id=${encodeURIComponent(propertyId)}`,
      { method: "POST", body: JSON.stringify(copy) },
    )
  },

  regenerateGuidebookWelcome(propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; draft?: { headline: string; message: string; hero_message: string } }>(
      `/app/guidebook-welcome/regenerate?property_id=${encodeURIComponent(propertyId)}`,
      { method: "POST", body: JSON.stringify({}) },
    )
  },

  revertGuidebookWelcome(propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; mode?: string }>(
      `/app/guidebook-welcome/revert?property_id=${encodeURIComponent(propertyId)}`,
      { method: "POST", body: JSON.stringify({}) },
    )
  },

  // Revenue gap detection
  getRevenueGaps(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      gaps?: Array<{ start: string; end: string; nights: number; open_ended: boolean }>
      adr_recent?: number | null
      window_days?: number
      has_bookings?: boolean
    }>(`/app/revenue-gaps?property_id=${encodeURIComponent(propertyId)}`)
  },

  // Opening Rules
  getOpeningRules(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; source?: string; rules?: Record<string, unknown> }>(
      `/app/opening-rules${params}`,
    )
  },

  saveOpeningRules(rules: Record<string, unknown>, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/opening-rules${params}`,
      { method: "PUT", body: JSON.stringify(rules) },
    )
  },

  // Property Settings
  getPropertySettings(propertyId?: string) {
    const url = propertyId
      ? `/api/properties/${encodeURIComponent(propertyId)}/settings`
      : `/app/property`
    return requestWithRefresh<{ ok: boolean; settings?: Record<string, unknown> }>(url)
  },

  savePropertySettings(settings: Record<string, unknown>, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; settings?: Record<string, unknown>; warning?: string }>(
      `/app/property${params}`,
      { method: "PUT", body: JSON.stringify(settings) },
    )
  },

  // TV Pairing
  resolveTvPairing(pin: string, propertyId: string, mode: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/tv/pair/resolve`,
      { method: "POST", body: JSON.stringify({ pin, property_id: propertyId, mode }) },
    )
  },

  // Weather confirmation
  getWeatherCurrent(propertyId: string, lat?: number, lon?: number, tz?: string) {
    const params = new URLSearchParams()
    if (lat != null) params.set("lat", String(lat))
    if (lon != null) params.set("lon", String(lon))
    if (tz) params.set("tz", tz)
    const qs = params.toString() ? `?${params.toString()}` : ""
    return requestWithRefresh<{
      ok: boolean
      weather?: {
        available: boolean
        source?: string
        timezone?: string
        current?: {
          temperature_c: number
          weather_code: number
          weather_label: string
          wind_speed_ms: number | null
          wind_gusts_ms: number | null
          humidity_pct: number | null
          cloud_cover_pct: number | null
          is_day: boolean
        }
        daily?: {
          sunrise: string | null
          sunset: string | null
          temp_max_c: number | null
          temp_min_c: number | null
        }
      }
    }>(`/api/properties/${propertyId}/weather/current${qs}`)
  },

  // Property creation (onboarding)
  fetchPmsUnits(apiKey: string, provider?: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      provider?: string
      count?: number
      units?: Array<{ id: string; name: string; rooms?: number }>
    }>("/app/property", {
      method: "POST",
      body: JSON.stringify({ public_api_key: apiKey, ...(provider ? { pms_provider: provider } : {}) }),
    })
  },

  // Sync PMS units using stored API key (no key needed from frontend)
  syncPmsUnits(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      provider?: string
      count?: number
      api_key_source?: string
      units?: Array<{ id: string; name: string; rooms?: number }>
    }>(`/app/property${params}`, {
      method: "POST",
      body: JSON.stringify({}),
    })
  },

  createPropertyFromUnit(data: {
    unit_id: string
    unit_name: string
    pms_provider?: string
    public_api_key?: string
    timezone?: string
    image_url?: string
  }, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      created?: boolean
      reason?: string
      property?: { id: string; name: string; slug?: string }
    }>(`/app/property${params}`, {
      method: "POST",
      body: JSON.stringify({ action: "create_property_from_unit", ...data }),
    })
  },

  createManualProperty(data: { name: string; timezone?: string; address?: string }) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      created?: boolean
      property?: { id: string; name: string; slug?: string }
    }>("/app/property", {
      method: "POST",
      body: JSON.stringify({ action: "create_manual_property", ...data }),
    })
  },

  purgePmsData(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      purged?: { bookings: number; sync_state: number }
    }>(`/app/property/purge-pms?property_id=${propertyId}`, {
      method: "POST",
      body: JSON.stringify({ confirm: "purge" }),
    })
  },

  // Research
  getResearch(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      source?: string
      in_progress?: boolean
      active_progress?: Record<string, unknown> | null
      report?: Record<string, unknown>
      public_code?: string
      public_message?: string
    }>(`/app/research${params}`)
  },

  runResearch(params: Record<string, unknown>, propertyId?: string) {
    const qs = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      run_id?: string
      run_status?: string
      in_progress?: boolean
      summary?: Record<string, unknown>
      progress?: Record<string, unknown> | null
    }>(`/app/research${qs}`, {
      method: "POST",
      body: JSON.stringify(params),
    })
  },

  // Calendar
  getCalendarFeeds(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; feeds?: Array<Record<string, unknown>> }>(
      `/app/calendar/feeds${params}`,
    )
  },

  saveCalendarFeed(feed: Record<string, unknown>, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; feed?: Record<string, unknown> }>(
      `/app/calendar/feeds${params}`,
      { method: "POST", body: JSON.stringify(feed) },
    )
  },

  deleteCalendarFeed(feedId: string, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/calendar/feeds${params}`,
      { method: "DELETE", body: JSON.stringify({ id: feedId, feed_id: feedId }) },
    )
  },

  syncCalendar(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; results?: Array<Record<string, unknown>> }>(
      `/app/calendar/sync${params}`,
      { method: "POST" },
    )
  },

  getCalendarAvailability(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; availability?: Array<Record<string, unknown>> }>(
      `/app/calendar/availability${params}`,
    )
  },

  getCalendarSyncHistory(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      runs?: Array<Record<string, unknown>>
      history?: Array<Record<string, unknown>>
    }>(
      `/app/calendar/sync-history${params}`,
    )
  },

  // Listings
  getListings(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; listings?: Array<Record<string, unknown>>; count?: number }>(
      `/app/listings${params}`,
    )
  },

  createListing(data: { name: string; pms_unit_ref?: string }, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; listing?: Record<string, unknown> }>(
      `/app/listings${params}`,
      { method: "POST", body: JSON.stringify(data) },
    )
  },

  updateListing(id: string, data: { name: string; pms_unit_ref?: string }, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string; listing?: Record<string, unknown> }>(
      `/app/listings/${id}${params}`,
      { method: "PUT", body: JSON.stringify(data) },
    )
  },

  deleteListing(id: string, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/listings/${id}${params}`,
      { method: "DELETE" },
    )
  },

  getListingsHealth(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      listings?: Array<{
        id: string
        name: string
        pms_unit_ref?: string | null
        is_default: boolean
        feeds_active: number
        feeds_error: number
        last_sync_at: string | null
        last_sync_ok: boolean
        next_booking: {
          start_date: string
          end_date: string
          provider?: string | null
          summary?: string
          guests?: number | null
        } | null
        alerts: Array<{ type: string; message: string }>
      }>
      pms_connected?: boolean
      pms_provider?: string | null
    }>(`/app/listings/health${params}`)
  },

  // Config copy between properties (preview/apply)
  previewConfigCopy(
    payload: {
      config_type: "places" | "house-rules"
      target_property_ids: string[]
      strategy?: "merge" | "replace"
    },
    propertyId?: string,
  ) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      code?: string
      mode?: "preview"
      config_type?: string
      strategy?: string
      source_property?: { id: string; name: string }
      targets?: Array<Record<string, unknown>>
      summary?: Record<string, unknown>
      preview_digest?: string
      preview_token?: string
      preview_expires_at?: string
      cannot_undo?: boolean
    }>(`/app/config-copy${params}`, {
      method: "POST",
      body: JSON.stringify({ ...payload, mode: "preview" }),
    })
  },

  applyConfigCopy(
    payload: {
      config_type: "places" | "house-rules"
      target_property_ids: string[]
      strategy?: "merge" | "replace"
      preview_token: string
    },
    propertyId?: string,
  ) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      code?: string
      mode?: "apply"
      config_type?: string
      strategy?: string
      source_property?: { id: string; name: string }
      targets?: Array<Record<string, unknown>>
      summary?: Record<string, unknown>
      preview_digest?: string
      applied_at?: string
      cannot_undo?: boolean
      failed_property_id?: string
      applied_targets?: Array<Record<string, unknown>>
    }>(`/app/config-copy${params}`, {
      method: "POST",
      body: JSON.stringify({ ...payload, mode: "apply" }),
    })
  },

  // Audit Log
  getAuditEvents(params: {
    property_id?: string
    actor_id?: string
    action?: string
    from?: string
    to?: string
    cursor?: string
    limit?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.property_id) qs.set("property_id", params.property_id)
    if (params.actor_id) qs.set("actor_id", params.actor_id)
    if (params.action) qs.set("action", params.action)
    if (params.from) qs.set("from", params.from)
    if (params.to) qs.set("to", params.to)
    if (params.cursor) qs.set("cursor", params.cursor)
    if (params.limit) qs.set("limit", String(params.limit))
    const q = qs.toString()
    return requestWithRefresh<{
      ok: boolean
      events?: Array<{
        id: string
        timestamp: string
        request_id: string
        actor_id: string
        actor_email: string
        property_id: string
        action: string
        resource: string
        resource_id: string
      }>
      has_more?: boolean
      next_cursor?: string
    }>(`/app/audit${q ? `?${q}` : ""}`)
  },

  getAuditEvent(eventId: string) {
    return requestWithRefresh<{
      ok: boolean
      event?: {
        id: string
        timestamp: string
        request_id: string
        actor_id: string
        actor_email: string
        property_id: string
        action: string
        resource: string
        resource_id: string
        diff: Record<string, unknown>
        ip_address: string
        user_agent: string
      }
    }>(`/app/audit/${encodeURIComponent(eventId)}`)
  },

  // Health
  getHealth(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      health?: {
        property_id: string
        checked_at: string
        calendar: {
          status: string
          feeds_total: number
          feeds_active: number
          feeds_with_errors: number
          last_sync: {
            id: string
            status: string
            started_at: string
            finished_at: string
            feeds_attempted: number
            feeds_success: number
            feeds_failed: number
            events_imported: number
            duration_ms: number
          } | null
          feeds: Array<{
            id: string
            provider: string
            label: string
            is_active: boolean
            error_count: number
            last_sync_at: string
            last_success_at: string
            last_error: string
          }>
        }
        research: {
          status: string
          last_run: {
            run_id: string
            status: string
            started_at: string
            finished_at: string
            places_scanned: number
            places_completed: number
            places_failed: number
            public_code: string
            public_message: string
          } | null
        }
        listings: {
          status: string
          count: number
          items: Array<{ id: string; name: string; is_default: boolean }>
        }
        auth: {
          status: string
          active_sessions: number
        }
        rate_limits: {
          has_active_blocks: boolean
          blocked_keys: number
        }
        recent_activity: Array<{
          id: string
          timestamp: string
          action: string
          resource: string
          actor_email: string
          request_id: string
        }>
        explanations?: Array<{
          card: string
          reason_code: string
          safe_summary: string
          suggested_actions: Array<{ label: string; deep_link: string }>
          last_seen_at: string
        }>
      }
    }>(`/app/health${params}`)
  },

  // Jobs
  getJobs(params: {
    property_id?: string
    job_type?: string
    status?: string
    from?: string
    to?: string
    cursor?: string
    limit?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.property_id) qs.set("property_id", params.property_id)
    if (params.job_type) qs.set("job_type", params.job_type)
    if (params.status) qs.set("status", params.status)
    if (params.from) qs.set("from", params.from)
    if (params.to) qs.set("to", params.to)
    if (params.cursor) qs.set("cursor", params.cursor)
    if (params.limit) qs.set("limit", String(params.limit))
    const q = qs.toString()
    return requestWithRefresh<{
      ok: boolean
      jobs?: Array<{
        id: string
        request_id: string
        property_id: string
        job_type: string
        status: string
        started_at: string
        finished_at: string
        duration_ms: number
        attempt: number
        max_attempts: number
        actor_email: string
        error_code: string
        error_message: string
      }>
      has_more?: boolean
      next_cursor?: string
    }>(`/app/jobs${q ? `?${q}` : ""}`)
  },

  getJob(jobId: string) {
    return requestWithRefresh<{
      ok: boolean
      job?: {
        id: string
        request_id: string
        property_id: string
        job_type: string
        status: string
        started_at: string
        finished_at: string
        duration_ms: number
        attempt: number
        max_attempts: number
        parent_job_id: string
        actor_id: string
        actor_email: string
        error_code: string
        error_message: string
        metadata: Record<string, unknown>
        events: Array<{
          id: number
          ts: string
          level: string
          event: string
          data: Record<string, unknown>
        }>
      }
    }>(`/app/jobs/${encodeURIComponent(jobId)}`)
  },

  retryJob(jobId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      code?: string
      job_id?: string
      status?: string
    }>(`/app/jobs/${encodeURIComponent(jobId)}/retry`, { method: "POST" })
  },

  // Alerts
  getAlertSubscriptions(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      subscriptions?: Array<{
        id: string
        type: string
        target: string
        enabled: number | boolean
        severity_threshold: string
        created_at: string
        updated_at: string
      }>
    }>(`/app/alerts${params}`)
  },

  upsertAlertSubscription(data: {
    id?: string
    type: string
    target: string
    enabled?: boolean
    severity_threshold?: string
  }, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      subscription?: { id: string; type: string; target: string; enabled: boolean; severity_threshold: string }
    }>(`/app/alerts${params}`, { method: "POST", body: JSON.stringify(data) })
  },

  deleteAlertSubscription(subId: string, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/alerts/${encodeURIComponent(subId)}${params}`,
      { method: "DELETE" },
    )
  },

  getAlertEvents(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      events?: Array<{
        id: string
        ts: string
        severity: string
        event_type: string
        summary: string
        delivery_status: string
        delivered_at: string
      }>
    }>(`/app/alerts/events${params}`)
  },

  // Members (RBAC)
  getMembers(propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      members?: Array<{
        user_id: string
        email: string
        name: string
        role: string
        is_owner: boolean
        created_at: string
      }>
    }>(`/app/members${params}`)
  },

  addMember(data: { email: string; role: string }, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      member?: { user_id: string; email: string; name: string; role: string }
    }>(`/app/members${params}`, { method: "POST", body: JSON.stringify(data) })
  },

  updateMemberRole(userId: string, role: string, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/members/${encodeURIComponent(userId)}${params}`,
      { method: "PUT", body: JSON.stringify({ role }) },
    )
  },

  removeMember(userId: string, propertyId?: string) {
    const params = propertyId ? `?property_id=${propertyId}` : ""
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/members/${encodeURIComponent(userId)}${params}`,
      { method: "DELETE" },
    )
  },

  // Property deletion (soft-delete)
  deleteProperty(propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/app/property?property_id=${encodeURIComponent(propertyId)}`,
      { method: "DELETE" },
    )
  },

  // Stay Strategy
  getStrategyOverview(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      has_booking?: boolean
      strategy?: {
        summary: string
        sections?: { arrival: string; during: string; departure: string } | null
        stay_profile: string
        party_profile: string
        night_mode: boolean
        nights: number
        adults: number
        children: number
        check_in: string
        check_out: string
        generated_at: string
        stale: boolean
        source?: string
        powered_by_ai?: boolean
        generated_by_model?: string
      } | null
      error?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/strategy/overview`)
  },

  refreshStrategyOverview(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      has_booking?: boolean
      strategy?: {
        summary: string
        sections?: { arrival: string; during: string; departure: string } | null
        stay_profile: string
        party_profile: string
        night_mode: boolean
        nights: number
        adults: number
        children: number
        check_in: string
        check_out: string
        generated_at: string
        stale: boolean
        source?: string
        powered_by_ai?: boolean
        generated_by_model?: string
      } | null
      error?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/strategy/overview`, {
      method: "POST",
    })
  },

  getPropertyOverview(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      property?: {
        id: string
        name: string
        timezone?: string
        render_key?: string
        guidebook_url?: string
      }
      health?: {
        places: "healthy" | "warning" | "error" | "neutral"
        research: "healthy" | "warning" | "error" | "neutral"
        calendar: "healthy" | "warning" | "error" | "neutral"
        pms: "healthy" | "warning" | "error" | "neutral"
      }
      listingsHealth?: {
        listings: Array<{
          id: string
          name: string
          pms_unit_ref?: string | null
          is_default: boolean
          feeds_active: number
          feeds_error: number
          last_sync_at: string | null
          last_sync_ok: boolean
          next_booking: {
            start_date: string
            end_date: string
            provider?: string | null
            summary?: string
            guests?: number | null
          } | null
          alerts: Array<{ type: string; message: string }>
        }>
        pms_connected?: boolean
        pms_provider?: string | null
      }
      nextBooking?: {
        guest_name: string
        arrival_date: string
        departure_date: string
        nights: number
        adults?: number | null
        children?: number | null
        infants?: number | null
        source?: string
      } | null
      stayPhases?: {
        before_checkin?: { label: string; summary: string }
        arrival_day: { label: string; summary: string }
        during_stay: { label: string; summary: string }
        checkout_evening: { label: string; summary: string }
        checkout_morning: { label: string; summary: string }
      }
      noBookingHint?: string
      stayStrategy?: {
        has_booking: boolean
        strategy: {
          summary: string
          sections?: { arrival: string; during: string; departure: string } | null
          stay_profile: string
          party_profile: string
          night_mode: boolean
          nights: number
          adults: number
          children: number
          check_in: string
          check_out: string
          generated_at: string
          stale: boolean
          source?: string
          powered_by_ai?: boolean
          generated_by_model?: string
        } | null
      }
      systemStatus?: {
        checked_at: string
        rows: Array<{
          key: string
          label: string
          status: "healthy" | "warning" | "error" | "neutral" | string
          last_run_at?: string
          detail?: string
          configured?: boolean
          cta?: { label: string; path: string } | null
        }>
        failures: Array<{
          id: string
          source: string
          occurred_at: string
          summary: string
          detail?: string
        }>
        guidebook_accesses?: Array<{
          id: string
          created_at: string
          status: string
          actor_label?: string
          match_source?: string
          check_in_date?: string
          booking_id?: string
        }>
        notice?: string
      }
      recentActivity?: Array<{
        id: string
        timestamp: string
        label: string
        actor_email?: string
      }>
      recentActivityNotice?: string
      intelligence?: {
        available: boolean
        heroStats?: {
          totalStays: number
          totalNights: number
          totalGuestNights: number
          narrative: string
          secondaryNarrative: string
        }
        geographic?: {
          countries: Array<{ code: string; name: string; count: number; share: number }>
          domesticShare: number
          internationalShare: number
          topOrigin: string | null
          narrative: string
        }
        guestDistribution?: {
          segments: Array<{ label: string; percentage: number; tooltip: string }>
          narrative: string
        }
        behaviourMetrics?: Array<{ label: string; value: string }>
        aiSummary?: {
          title: string
          content: string
          updatedAt: string
        } | null
      }
      milestones?: {
        earned_recent: Array<{
          id: string
          label: string
          tier: 1 | 2 | 3
          category: string
          achieved_at: string
          next_target?: number | null
          next_label?: string | null
        }>
        closest: {
          id: string
          label: string
          narrative: string
          tier: 1 | 2 | 3
          category: string
          current: number
          target: number
        } | null
        total_earned: number
        data_sufficient: boolean
      }
      reviewTeaser?: ReviewTeaser
    }>(`/api/property/overview?propertyId=${encodeURIComponent(propertyId)}`)
  },

  getPropertyProgress(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      progress?: {
        earned_groups: Array<{
          category: string
          label: string
          items: Array<{
            id: string
            label: string
            tier: 1 | 2 | 3
            category: string
            achieved_at: string
            next_target?: number | null
            next_label?: string | null
          }>
        }>
        in_progress: Array<{
          id: string
          label: string
          narrative: string
          tier: 1 | 2 | 3
          category: string
          current: number
          target: number
        }>
        seasonal: {
          label: string
          season_label: string
          signals: Array<{ label: string; detected: boolean }>
          detected_count: number
          total_count: number
        } | null
        total_earned: number
        tier_breakdown: { t1: number; t2: number; t3: number }
        data_sufficient: boolean
      }
    }>(`/api/properties/${encodeURIComponent(propertyId)}/progress`)
  },

  triggerDisplayRender(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      rendered?: boolean
      cached?: boolean
      rendered_at?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display/render`, { method: "POST" })
  },

  // Display Behaviour — Fallback Messages
  getFallbackMessages(propertyId: string, lang?: string) {
    const params = lang ? `?lang=${encodeURIComponent(lang)}` : ""
    return requestWithRefresh<{
      ok: boolean
      fallbacks?: Array<{
        id: string
        property_id: string
        message_type: string
        lang: string
        headline: string
        message: string
        source: string
        updated_at: string
      }>
      source?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/fallbacks${params}`)
  },

  saveFallbackMessage(
    data: { message_type: string; lang: string; headline: string; message: string; source: string },
    propertyId: string,
  ) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      fallback?: Record<string, unknown>
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/fallbacks`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  },

  resetFallbackMessage(messageType: string, lang: string, propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/fallbacks`,
      { method: "DELETE", body: JSON.stringify({ message_type: messageType, lang }) },
    )
  },

  populateFallbackByAI(messageType: string, lang: string, propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      draft?: { headline: string; message: string }
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/fallbacks/populate`, {
      method: "POST",
      body: JSON.stringify({ message_type: messageType, lang }),
    })
  },

  // Display Behaviour — Night Mode
  getNightMode(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      config?: {
        enabled: boolean
        start_hour: number
        start_minute: number
        end_hour: number
        end_minute: number
        area_name: string
        area_type: string
      }
      source?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/night-mode`)
  },

  saveNightMode(config: Record<string, unknown>, propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      config?: Record<string, unknown>
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/night-mode`, {
      method: "PUT",
      body: JSON.stringify(config),
    })
  },

  // Display Behaviour — Trivia Pool
  getTriviaPool(propertyId: string, includeFacts?: boolean) {
    const qs = includeFacts ? "?include=facts" : ""
    return requestWithRefresh<{
      ok: boolean
      area_name?: string
      pools?: { sv: number; en: number; de: number }
      total?: number
      facts?: Array<{ lang: string; headline: string; fact: string }>
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/trivia-pool${qs}`)
  },

  generateTriviaPool(lang: string, propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      generated?: number
      saved?: number
      area_name?: string
      lang?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/display-behaviour/trivia-pool`, {
      method: "POST",
      body: JSON.stringify({ lang }),
    })
  },

  // TRMNL Integration
  getTrmnlIntegration(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      active: boolean
      created_at?: string
      last_used_at?: string | null
      token?: string | null
    }>(`/api/properties/${encodeURIComponent(propertyId)}/integrations/trmnl`)
  },

  generateTrmnlToken(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      token?: string
      warning?: string
      error?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/integrations/trmnl`, {
      method: "POST",
    })
  },

  revokeTrmnlToken(propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/properties/${encodeURIComponent(propertyId)}/integrations/trmnl`,
      { method: "DELETE" },
    )
  },

  // Platform Admin
  getPlatformUsers(params: {
    search?: string
    active?: string
    cursor?: string
    limit?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.search) qs.set("search", params.search)
    if (params.active) qs.set("active", params.active)
    if (params.cursor) qs.set("cursor", params.cursor)
    if (params.limit) qs.set("limit", String(params.limit))
    const q = qs.toString()
    return requestWithRefresh<{
      ok: boolean
      users?: Array<{
        id: string
        easy_id: string
        email: string
        name: string
        role: string
        is_active: boolean
        is_platform_admin: boolean
        property_count: number
        active_session_count: number
        created_at: string
        last_login_at: string
      }>
      has_more?: boolean
      next_cursor?: string | null
    }>(`/platform/users${q ? `?${q}` : ""}`)
  },

  getPlatformProperties(params: {
    search?: string
    tier?: string
    cursor?: string
    limit?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.search) qs.set("search", params.search)
    if (params.tier) qs.set("tier", params.tier)
    if (params.cursor) qs.set("cursor", params.cursor)
    if (params.limit) qs.set("limit", String(params.limit))
    const q = qs.toString()
    return requestWithRefresh<{
      ok: boolean
      properties?: Array<{
        id: string
        name: string
        slug: string
        tier: string
        timezone: string
        is_active: boolean
        owner_email: string
        owner_name: string
        member_count: number
        listing_count: number
        created_at: string
        updated_at: string
      }>
      has_more?: boolean
      next_cursor?: string | null
    }>(`/platform/properties${q ? `?${q}` : ""}`)
  },

  getPlatformStats() {
    return requestWithRefresh<{
      ok: boolean
      stats?: {
        users: { total: number; active: number; platform_admins: number }
        properties: { total: number; active: number }
        sessions: { active: number }
        recent_activity: Array<{ action: string; count: number }>
      }
    }>("/platform/stats")
  },

  // ---------------------------------------------------------------------------
  // Performance Dashboard
  // ---------------------------------------------------------------------------

  getPerformanceSummary(propertyId: string, params: {
    from: string
    to: string
    compare?: "prev" | "yoy" | "off"
    channel?: string
  }) {
    const qs = new URLSearchParams()
    qs.set("from", params.from)
    qs.set("to", params.to)
    if (params.compare && params.compare !== "off") qs.set("compare", params.compare)
    if (params.channel) qs.set("channel", params.channel)
    return requestWithRefresh<PerformanceSummaryResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/summary?${qs.toString()}`
    )
  },

  getPerformanceBookings(propertyId: string, params: {
    from: string
    to: string
    cursor?: string
    limit?: number
    sort?: string
    order?: string
    channel?: string
  }) {
    const qs = new URLSearchParams()
    qs.set("from", params.from)
    qs.set("to", params.to)
    if (params.cursor) qs.set("cursor", params.cursor)
    if (params.limit) qs.set("limit", String(params.limit))
    if (params.sort) qs.set("sort", params.sort)
    if (params.order) qs.set("order", params.order)
    if (params.channel) qs.set("channel", params.channel)
    return requestWithRefresh<PerformanceBookingsResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/bookings?${qs.toString()}`
    )
  },

  getPerformanceCalendar(propertyId: string, params: { from: string; to: string }) {
    return requestWithRefresh<PerformanceCalendarResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/calendar?from=${params.from}&to=${params.to}`
    )
  },

  syncPerformanceData(propertyId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; code?: string; sync_started?: boolean; bookings_synced?: number; retry_after?: number }>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/sync`,
      { method: "POST" }
    )
  },

  getPerformanceInsights(propertyId: string, params?: { from?: string; to?: string; force?: boolean }) {
    const p = params ? Object.entries(params).filter(([, v]) => Boolean(v)).map(([k, v]) => [k, String(v)]) as [string, string][] : []
    const qs = p.length ? new URLSearchParams(p).toString() : ""
    return requestWithRefresh<PerformanceInsightsResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/insights${qs ? `?${qs}` : ""}`
    )
  },

  dismissPerformanceInsight(propertyId: string, insightId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/insights/dismiss`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ insight_id: insightId }) }
    )
  },

  getPerformanceDrilldown(propertyId: string, params: {
    kpi: "revenue" | "occupancy" | "adr"
    from: string
    to: string
  }) {
    const qs = new URLSearchParams()
    qs.set("kpi", params.kpi)
    qs.set("from", params.from)
    qs.set("to", params.to)
    return requestWithRefresh<PerformanceDrilldownResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/drilldown?${qs.toString()}`
    )
  },

  getSeasonProfile(propertyId: string, force?: boolean) {
    const qs = force ? "?force=true" : ""
    return requestWithRefresh<SeasonProfileResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/season-profile${qs}`
    )
  },

  getRevenueForecast(propertyId: string) {
    return requestWithRefresh<RevenueForecastResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/performance/forecast`
    )
  },

  // Intelligence gate config (read-only for host dashboard)
  getIntelligenceGateConfig() {
    return requestWithRefresh<{
      ok: boolean
      config?: {
        ritual_enabled: boolean
        cooldown_minutes: number
        guidebook_grace_ms: number
      }
    }>("/api/intelligence-gate")
  },

  // Airbnb Reviews
  getAirbnbReviews(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      source: {
        id: string
        source: string
        source_url: string
        status: "pending" | "running" | "stale" | "ok" | "error"
        apify_run_id: string
        apify_dataset_id: string
        last_error: string
        last_synced_at: string
      } | null
      summary: {
        reviews_count: number
        average_rating: number | null
        category_scores?: AirbnbCategoryScores
      } | null
      reviews: Array<{
        id: string
        reviewer_name: string
        reviewer_image_url: string
        rating: number
        text: string
        localized_text: string
        language: string
        created_at: string
      }> | null
    }>(`/api/properties/${encodeURIComponent(propertyId)}/reviews/airbnb`)
  },

  importAirbnbReviews(propertyId: string, sourceUrl: string, opts?: { force?: boolean }) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      run_id?: string
      listing_run_id?: string
      job_run_id?: string
      skipped?: boolean
      reason?: string
      message?: string
      last_synced_hours_ago?: number
    }>(`/api/properties/${encodeURIComponent(propertyId)}/reviews/airbnb/import`, {
      method: "POST",
      body: JSON.stringify({ source_url: sourceUrl, force: opts?.force }),
    })
  },

  // Booking.com Reviews
  getBookingReviews(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      source: {
        id: string
        source: string
        source_url: string
        status: "pending" | "running" | "stale" | "ok" | "error"
        apify_run_id: string
        apify_dataset_id: string
        last_error: string
        last_synced_at: string
      } | null
      summary: BookingReviewSummary | null
      reviews: BookingReviewItem[] | null
    }>(`/api/properties/${encodeURIComponent(propertyId)}/reviews/booking`)
  },

  importBookingReviews(propertyId: string, sourceUrl: string, opts?: { force?: boolean }) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      run_id?: string
      job_run_id?: string
      skipped?: boolean
      reason?: string
      message?: string
      last_synced_hours_ago?: number
    }>(`/api/properties/${encodeURIComponent(propertyId)}/reviews/booking/import`, {
      method: "POST",
      body: JSON.stringify({ source_url: sourceUrl, force: opts?.force }),
    })
  },

  getReviewIntelligence(propertyId: string) {
    return requestWithRefresh<ReviewIntelligenceResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/reviews/intelligence`
    )
  },

  recomputeReviewIntelligence(propertyId: string) {
    return requestWithRefresh<ReviewIntelligenceResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/reviews/intelligence`,
      { method: "POST" }
    )
  },

  // --- Usage Dashboard ---

  getUsageDashboard(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      today?: {
        date: string
        used: number
        limit: number
        remaining: number
        pct: number
      }
      period?: {
        from: string
        to: string
        total_calls: number
        total_cost_usd: number
        total_input_tokens: number
        total_output_tokens: number
      }
      daily?: Array<{ day: string; calls: number; cost_usd: number }>
      by_surface?: Array<{ surface: string; calls: number; cost_usd: number }>
      by_model?: Array<{ model: string; calls: number; cost_usd: number }>
      error?: string
    }>(`/api/properties/${encodeURIComponent(propertyId)}/usage`)
  },

  // --- Listing Intelligence ---

  getListingAudit(propertyId: string) {
    return requestWithRefresh<ListingAuditResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/listings/audit`
    )
  },

  runListingAudit(propertyId: string) {
    return requestWithRefresh<ListingAuditRunResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/listings/audit`,
      { method: "POST" }
    )
  },

  runPhotoAnalysis(propertyId: string) {
    return requestWithRefresh<PhotoAnalysisResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/listings/photo-analysis`,
      { method: "POST" }
    )
  },

  generateListingContent(propertyId: string, params: {
    channel: "airbnb" | "booking" | "direct"
    target_season?: string | null
    content_type?: "titles" | "descriptions" | "both"
  }) {
    return requestWithRefresh<ListingContentGenResponse>(
      `/api/properties/${encodeURIComponent(propertyId)}/listings/generate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      }
    )
  },

  // Billing (per-account)
  createCheckoutSession(plan: string) {
    return requestWithRefresh<{ ok: boolean; checkout_url?: string; session_id?: string; error?: string }>(
      `/api/billing/checkout`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      }
    )
  },

  createPortalSession() {
    return requestWithRefresh<{ ok: boolean; portal_url?: string; error?: string }>(
      `/api/billing/portal`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    )
  },

  getBillingStatus() {
    return requestWithRefresh<{
      ok: boolean
      billing?: {
        demo_tier_switch_enabled?: boolean
        stripe_customer_id: string | null
        property_count: number
        volume_bracket: { min: number; max: number; count: number }
        subscription: BillingSubscription | null
      }
      error?: string
    }>(`/api/billing/status`)
  },

  // GDPR: Account deletion
  deleteAccount(confirmation: string) {
    return requestWithRefresh<{
      ok: boolean
      status?: string
      message?: string
      error?: string
      code?: string
      properties?: Array<{ id: string; name: string }>
    }>("/api/account/delete", {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    })
  },

  // GDPR: Data export (returns raw Response for file download)
  async exportAccountData(): Promise<{ ok: boolean; error?: string; blob?: Blob; filename?: string }> {
    const url = `${API_BASE_URL}/api/account/export`
    const headers = new Headers()
    headers.set("X-Request-Id", generateRequestId())
    const token = getAccessToken()
    if (token) headers.set("Authorization", `Bearer ${token}`)

    const res = await fetch(url, { headers, credentials: "include" })
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: `Request failed (${res.status})` }))
      return { ok: false, error: data.error || `Export failed (${res.status})` }
    }
    const disposition = res.headers.get("Content-Disposition") || ""
    const match = disposition.match(/filename="?([^"]+)"?/)
    const filename = match?.[1] || `stayframe-export-${new Date().toISOString().split("T")[0]}.json`
    const blob = await res.blob()
    return { ok: true, blob, filename }
  },

  // GDPR: Guest data anonymisation
  anonymiseGuests(propertyId: string, beforeDate: string, confirmation: string) {
    return requestWithRefresh<{
      ok: boolean
      anonymised?: { bookings: number; bookings_normalized: number; calendar_events: number }
      before_date?: string
      error?: string
      code?: string
    }>(`/app/property/anonymise-guests?property_id=${encodeURIComponent(propertyId)}`, {
      method: "POST",
      body: JSON.stringify({ confirmation, before_date: beforeDate }),
    })
  },

  submitPageFeedback(propertyId: string, data: {
    page_slug: string
    score: number
    answers?: PageFeedbackAnswer[]
  }) {
    return requestWithRefresh<{
      ok: boolean
      request_id: string
      error?: string
      code?: string
    }>(`/app/property/${encodeURIComponent(propertyId)}/feedback`, {
      method: "POST",
      body: JSON.stringify({
        page_slug: data.page_slug,
        score: data.score,
        answers: Array.isArray(data.answers) ? data.answers : [],
      }),
    })
  },

  getAdminFeedbackSummary(days = 90) {
    const windowDays = Math.min(365, Math.max(1, Math.floor(days || 90)))
    return requestWithRefresh<{
      ok: boolean
      request_id: string
      data?: AdminFeedbackSummary
      error?: string
      code?: string
    }>(`/api/admin/feedback/summary?days=${windowDays}`)
  },

  // ---- Activation Events (Onboarding Checklist) ----

  getActivationEvents(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      events: Array<{ event_key: string; completed_at: string }>
    }>(`/api/properties/${encodeURIComponent(propertyId)}/activation`)
  },

  recordActivationEvent(propertyId: string, eventKey: string) {
    return requestWithRefresh<{
      ok: boolean
      event: { event_key: string; completed_at: string }
    }>(`/api/properties/${encodeURIComponent(propertyId)}/activation`, {
      method: "POST",
      body: JSON.stringify({ event_key: eventKey }),
    })
  },

}

export interface PageFeedbackAnswer {
  question: string
  selected_option: string
  other_text?: string | null
}

export interface AdminFeedbackSummary {
  pages: Record<string, {
    nps: number
    total: number
    detractors: number
    passives: number
    promoters: number
  }>
  recent: Array<{
    id: number
    page_slug: string
    score: number
    segment: string
    user_email: string
    answers: PageFeedbackAnswer[]
    created_at: string
  }>
  top_issues: Array<{ option: string; page: string; count: number }>
  top_praise: Array<{ option: string; page: string; count: number }>
}

export interface BillingSubscription {
  id: string
  plan: string
  status: string
  is_trialing: boolean
  trial_ends_at: string | null
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  stripe_subscription_id: string | null
  price_cents: number | null
}

// ---------------------------------------------------------------------------
// Performance Dashboard types
// ---------------------------------------------------------------------------

export interface PerformanceKpi {
  value: number
  currency?: string
  delta?: number | null
}

export interface PerformanceTrendPoint {
  period: string
  revenue: number
  occupancy: number
  adr: number
  revpar: number
  bookings: number
  booked_nights?: number
}

export interface PerformanceChannel {
  name: string
  bookings: number
  revenue: number
  nights: number
  pct: number
  adr: number
  cancellation_rate?: number | null
  lead_time_avg?: number | null
}

export interface PerformancePacing {
  current_revenue: number
  current_nights: number
  last_year_revenue: number
  last_year_nights: number
  delta_pct: number | null
  elapsed_days: number
  remaining_days: number
  total_days: number
  ly_period: { from: string; to: string }
  currency: string
  new_last_30d_total?: number
  pace_kpi_deltas?: {
    revenue: number | null
    occupancy: number | null
    adr: number | null
    revpar: number | null
    bookings: number | null
    booked_nights: number | null
  } | null
  pace_by_month?: Array<{
    month: string
    current_rotb: number
    ly_rotb: number
    pct_of_ly: number | null
    new_last_30d: number
    current_nights: number
    ly_nights: number
    current_bookings?: number
    ly_bookings?: number
  }>
}

export interface PerformanceSummaryResponse {
  ok: boolean
  error?: string
  period?: { from: string; to: string }
  sync?: { last_sync_at: string | null; status: string; bookings_count: number }
  kpis?: {
    revenue: PerformanceKpi
    occupancy: PerformanceKpi
    adr: PerformanceKpi
    revpar: PerformanceKpi
    bookings: PerformanceKpi
    booked_nights?: PerformanceKpi
    avg_los: PerformanceKpi
    avg_lead_time: PerformanceKpi
    cancellation_rate: PerformanceKpi
  }
  trend?: PerformanceTrendPoint[]
  channels?: PerformanceChannel[]
  all_channels?: string[]
  pacing?: PerformancePacing | null
  channel_filter_note?: string
  comparison?: {
    period: { from: string; to: string }
    kpis: Record<string, PerformanceKpi>
    trend?: PerformanceTrendPoint[]
  } | null
  demand_behaviour?: DemandBehaviour | null
}

export interface DemandBehaviourBucket {
  label: string
  pct: number
}

export interface DemandBehaviour {
  lead_time_distribution: DemandBehaviourBucket[]
  stay_length_distribution: DemandBehaviourBucket[]
  weekend_pct: number
}

export interface SeasonProfileResponse {
  ok: boolean
  visible?: boolean
  classification?: string
  peak_season?: string | null
  ratio?: number | null
  confidence?: string
  computed_at?: string
  from_cache?: boolean
  peak_months?: number[]
  shoulder_months?: number[]
  off_season_months?: number[]
  strength?: string | null
  hemisphere?: string
}

export interface PerformanceBooking {
  id: string
  guest_name: string
  check_in: string
  check_out: string
  nights: number
  source: string
  status: string
  total_amount: number
  currency: string
  adults: number
  children: number
  infants: number
  is_cancelled: number
  is_confirmed: number
  is_owner_stay: number
  created_at: string
}

export interface PerformanceBookingsResponse {
  ok: boolean
  error?: string
  bookings?: PerformanceBooking[]
  has_more?: boolean
  next_cursor?: string | null
}

export interface PerformanceCalendarDay {
  date: string
  status: "booked" | "available" | "blocked"
  booking_id?: string
  rate?: number
}

export interface PerformanceCalendarResponse {
  ok: boolean
  error?: string
  days?: PerformanceCalendarDay[]
}

export interface PerformanceInsight {
  id: string
  type: string
  severity: "warning" | "opportunity" | "info"
  title: string
  observation: string
  impact: string
  action: string
  confidence: string
  data: Record<string, unknown>
  created_at: string
}

export interface SeasonContext {
  classification: string
  peak_season: string | null
  ratio: number | null
  strength: string | null
  peak_months: number[]
  shoulder_months: number[]
  off_season_months: number[]
  current_month_local: number
  current_year_month_local: string
  confidence: string
  hemisphere: string
}

export interface PerformanceInsightsResponse {
  ok: boolean
  error?: string
  insights?: PerformanceInsight[]
  // AI-powered response fields (present when FF_AI_INSIGHTS=true)
  ai_powered?: boolean
  cached?: boolean
  model?: string
  result?: InsightResult
  season_context?: SeasonContext | null
  _fallback_reason?: string
  fallback_reason?: string
}

// ---------------------------------------------------------------------------
// AI Insights types (FF_AI_INSIGHTS)
// ---------------------------------------------------------------------------

export interface InsightResultMeta {
  model: string
  generated_at: string
  schema_version: string
}

export interface InsightItem {
  id: string
  severity: "warning" | "opportunity" | "info"
  title: string
  observation: string
  confidence: "high" | "medium" | "low"
  evidence_paths: string[]
}

export interface InsightDriver {
  hypothesis: string
  confidence: "high" | "medium" | "low"
  evidence_paths: string[]
}

export interface InsightRecommendation {
  action: string
  priority: "act_now" | "monitor"
  metric_target: string | null
  confidence: "high" | "medium" | "low"
}

export interface InsightHumanSummary {
  what_matters_now: string[]
  drivers: string[]
  actions_this_week: string[]
}

export interface InsightResult {
  meta: InsightResultMeta
  warnings: string[]
  checks: Array<{ check: string; passed: boolean; detail: string }>
  insights: InsightItem[]
  drivers: InsightDriver[]
  recommendations: InsightRecommendation[]
  experiments: unknown[]
  human_summary: InsightHumanSummary
}

export interface PerformanceDrilldownResponse {
  ok: boolean
  error?: string
  kpi?: string
  currency?: string
  // Revenue drilldown
  bookings?: PerformanceBooking[]
  channel_revenue?: Array<{ name: string; revenue: number; bookings: number; pct: number }>
  total_revenue?: number
  // Occupancy drilldown
  breakdown?: { booked_nights: number; available_nights: number; blocked_nights: number; total_days: number }
  monthly_occupancy?: Array<{ month: string; booked: number; available: number; pct: number }>
  calendar?: PerformanceCalendarDay[]
  // ADR drilldown
  adr_by_channel?: Array<{ channel: string; adr: number; bookings: number; revenue: number }>
  adr_by_dow?: Array<{ dow: string; avg_adr: number; count: number }>
  adr_distribution?: Array<{ bucket: string; count: number }>
}

// ---------------------------------------------------------------------------
// Revenue Forecast types
// ---------------------------------------------------------------------------

export interface ForecastMonthly {
  month: string
  rotb: number
  projected_fill_revenue: number
  forecast: number
  low: number
  high: number
  confidence: "actual" | "high" | "medium" | "low"
  pct_on_books: number
  vs_ly_actual: number | null
  is_actual: boolean
  available_nights: number
  booked_nights: number
}

export interface ForecastVolatility {
  prior_forecast: number
  current_forecast: number
  delta: number
  delta_pct: number
  is_volatile: boolean
  period_days: number
}

export interface ForecastInterpretation {
  summary: string
  risks: string | null
  model: string
  generated_at: string
}

export interface ForecastData {
  forecast_date: string
  forecast_year: number
  annual_forecast: number
  annual_low: number
  annual_high: number
  ytd_actual: number
  remaining_forecast: number
  confidence: "high" | "medium" | "low" | "insufficient"
  method: string
  currency: string
  monthly: ForecastMonthly[]
  assumptions: Record<string, unknown>
  data_quality: {
    history_months: number
    data_years: number
    season_classification: string
    season_confidence: string
    capture_curve_months: number
    warnings: string[]
  }
  volatility: ForecastVolatility | null
  ly_total: number
  vs_ly_pct: number | null
  computed_at: string
  interpretation: ForecastInterpretation | null
}

export interface RevenueForecastResponse {
  ok: boolean
  error?: string
  forecast: ForecastData | null
  message?: string
}

// ---------------------------------------------------------------------------
// Review Intelligence types
// ---------------------------------------------------------------------------

export interface ReviewTheme {
  sentiment: "positive" | "neutral" | "negative"
  strength: number
  mentions: number
  evidence: string[]
}

export interface ReviewStrength {
  theme: string
  signal: string
  confidence: "high" | "medium" | "low"
  evidence_count: number
}

export interface ReviewRisk {
  theme: string
  signal: string
  severity: "low" | "medium" | "high"
  evidence: string
  actionable: boolean
}

export interface ReviewNumericContext {
  review_velocity_per_quarter: number
  newest_review_days: number
  oldest_review_days: number
  rating_consistency: number
  rating_stdev: number
  trend_diff: number
  avg_newest_third: number
  avg_oldest_third: number
  total_reviews: number
  trend_window_days?: number | null      // 90 if using windows, null if using thirds
  recent_window_count?: number | null    // reviews in last 90 days
  prior_window_count?: number | null     // reviews in prior 90 days
}

export interface ReviewTemperatureComponents {
  velocity_score: number
  recency_weight: number
  sentiment_consistency: number
  trend_bonus: number
}

export interface BookingReviewItem {
  id: string
  reviewer_name: string
  reviewer_avatar: string
  reviewer_location: string
  traveler_type: string
  rating: number
  review_title: string
  liked_text: string
  disliked_text: string
  room_info: string
  review_language: string
  check_in_date: string
  check_out_date: string
  number_of_nights: number
  created_at: string
}

export interface AirbnbCategoryScores {
  accuracy: number | null
  check_in: number | null
  cleanliness: number | null
  communication: number | null
  location: number | null
  value: number | null
}

export interface BookingCategoryScores {
  staff: number | null
  facilities: number | null
  cleanliness: number | null
  comfort: number | null
  value: number | null
  location: number | null
  wifi: number | null
}

export interface BookingReviewSummary {
  hotel_rating: number | null
  hotel_rating_label: string
  hotel_reviews_total: number
  fetched_count: number
  computed_average: number | null
  category_scores: BookingCategoryScores
}

export interface ReviewAnalysis {
  themes: Record<string, ReviewTheme>
  strengths: ReviewStrength[]
  risks: ReviewRisk[]
  identity: string
  teaser_sentence?: string
  featured_reviews?: Array<{
    reviewer_name: string
    date: string
    rating: number
    source?: "airbnb" | "booking"
    reason: string
  }>
  sentiment_trend: "improving" | "stable" | "softening"
  sentiment_trend_explanation: string
  rebooking_signals: {
    explicit_mentions: number
    implicit_signals: number
    strength: "strong" | "moderate" | "weak" | "none"
  }
  guest_types: Array<{ type: string; count: number; share: number }>
  booking_insights?: {
    category_summary: string
    guest_profile: string
  }
  cross_platform_note?: string
  _numeric_context?: ReviewNumericContext
  _booking_numeric_context?: ReviewNumericContext
  _temperature?: { components: ReviewTemperatureComponents }
}

export interface ReviewFallback {
  average_rating: number | null
  rating_trend: "improving" | "stable" | "declining" | null
  review_count: number
  review_velocity: number | null
  rating_consistency: number | null
  llm_available: false
}

export interface ReviewIntelligenceResponse {
  ok: boolean
  available: boolean
  stale: boolean
  computing?: boolean
  analysis?: ReviewAnalysis
  reputation_score?: number
  temperature_band?: string
  review_count: number
  computed_at?: string
  model_used?: string
  fallback?: ReviewFallback
}

export interface ReviewTeaser {
  available: boolean
  average_rating: number | null
  reviews_count: number
  reputation_score: number | null
  temperature_band: string | null
  computed_at: string | null
  last_review_at: string | null
  teaser_sentence?: string | null
}

// ---------------------------------------------------------------------------
// Listing Intelligence types
// ---------------------------------------------------------------------------

export interface ListingAuditDimension {
  score: number
  label: "Good" | "Needs Work" | "Poor" | "Critical"
  description: string
}

export interface ListingAuditIssue {
  id: string
  severity: "critical" | "warning" | "suggestion"
  category: "Content" | "Amenities" | "Reviews" | "Seasonal"
  title: string
  description: string
  source: string
  fixType: "title" | "description" | "amenities" | "seasonal" | null
}

export interface ListingAlignmentRow {
  claim: string
  evidence: string
  status: "confirmed" | "unconfirmed" | "missed"
}

export interface ListingSeasonalMismatch {
  text: string
}

export interface PhotoAnalysisCoverPhoto {
  quality: string
  composition: string
  first_impression: string
}

export interface PhotoAnalysisRoomCoverage {
  rooms_shown: string[]
  rooms_missing: string[]
  coverage_pct: number
}

export interface PhotoAnalysisIssue {
  severity: "critical" | "warning" | "suggestion"
  title: string
  description: string
  affected_photos?: number[]
}

export interface PhotoAnalysisOrientationBalance {
  landscape_count: number
  portrait_count: number
  recommendation: string
}

export interface PhotoAnalysisResult {
  photo_score: number
  photo_label: string
  photo_summary: string
  cover_photo: PhotoAnalysisCoverPhoto
  room_coverage: PhotoAnalysisRoomCoverage
  photo_issues: PhotoAnalysisIssue[]
  strengths: string[]
  orientation_balance: PhotoAnalysisOrientationBalance
  analysed_at: string
  photo_count: number
}

export interface PhotoAnalysisResponse {
  ok: boolean
  photo_analysis?: PhotoAnalysisResult
  error?: string
  code?: string
  upgrade_to?: string
}

export interface ListingAuditData {
  composite_score: number
  letter_grade: string
  score_delta: number | null
  dimensions: {
    content: ListingAuditDimension
    amenities: ListingAuditDimension
    reviews: ListingAuditDimension
    seasonal: ListingAuditDimension
    photo: ListingAuditDimension
  }
  issues: ListingAuditIssue[]
  alignment: ListingAlignmentRow[]
  seasonal_mismatches: ListingSeasonalMismatch[]
  audit_summary: string
  computed_at: string
  model_used: string
  photo_analysis?: PhotoAnalysisResult | null
}

export interface ListingAuditResponse {
  ok: boolean
  available: boolean
  audit: ListingAuditData | null
}

export interface ListingAuditRunResponse {
  ok: boolean
  audit: ListingAuditData | null
  reason?: string
  code?: string
  error?: string
  upgrade_to?: string
}

export interface ListingContentTitle {
  text: string
  rationale: string
  sources: string[]
  char_count: number
}

export interface ListingContentDescription {
  text: string
  rationale: string
  sources: string[]
  char_count: number
}

export interface ListingContentGenResponse {
  ok: boolean
  titles: ListingContentTitle[]
  descriptions: ListingContentDescription[]
  model_used: string
  error?: string
  code?: string
  upgrade_to?: string
}
